import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import { ClientReview, UserMap } from '../../types';
import { FaStar } from 'react-icons/fa';
import './UserStats.scss';

interface Props {
  reviews: ClientReview[];
  userMap: UserMap;
  startDate?: string;
  endDate?: string;
}

interface Leader {
  user_id: number;
  name: string;
  am: number;
  ex: number;
  all: number;
}

const UserStats: React.FC<Props> = ({ reviews, userMap, startDate, endDate }) => {
  const [option, setOption] = useState<any>(null);
  const [topAm, setTopAm] = useState<Leader[]>([]);
  const [topEx, setTopEx] = useState<Leader[]>([]);
  const [topAll, setTopAll] = useState<Leader[]>([]);
  const [textColor, setTextColor] = useState('#6b6b6b');
  const [colorMode, setColorMode] = useState(localStorage.getItem('colorMode') || 'dark');

  useEffect(() => {
    const updateTextColor = () => {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue('--text-color')
        .trim();
      if (color) {
        setTextColor(color);
      }
      const mode = localStorage.getItem('colorMode') || 'dark';
      setColorMode(mode);
    };
    
    updateTextColor();
    const observer = new MutationObserver(updateTextColor);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-color-mode', 'class']
    });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // 1) Only pedido_type_id === 1
    const data = reviews.filter(r => r.pedido_type_id === 1);
    if (!data.length) return;

    // 2) Decidir agrupación diaria, semanal, mensual o anual
    let groupType: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly';
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays <= 7) groupType = 'daily';
      else if (diffDays <= 31) groupType = 'weekly';
      else if (diffDays <= 366) groupType = 'monthly';
      else groupType = 'yearly';
    }

    // Helper para obtener la semana ISO (YYYY-WW)
    const getISOWeek = (dateStr: string) => {
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
      const week1 = new Date(d.getFullYear(), 0, 4);
      const weekNum =
        1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
      return `${d.getFullYear()}-W${weekNum.toString().padStart(2, '0')}`;
    };

    // 3) Agrupar por día, semana o mes y usuario
    const byGroupUser: Record<string, Record<number, number[]>> = {};
    data.forEach(r => {
      let groupKey: string;
      if (groupType === 'daily') {
        groupKey = r.timestamp.slice(0, 10); // "YYYY-MM-DD"
      } else if (groupType === 'weekly') {
        groupKey = getISOWeek(r.timestamp);
      } else if (groupType === 'monthly') {
        groupKey = r.timestamp.slice(0, 7); // "YYYY-MM"
      } else {
        groupKey = r.timestamp.slice(0, 4); // "YYYY"
      }
      if (!byGroupUser[groupKey]) byGroupUser[groupKey] = {};
      const avgUE =
        (r.object.amabilidad.rating + r.object.experiencia.rating) / 2;
      if (!byGroupUser[groupKey][r.user_id]) byGroupUser[groupKey][r.user_id] = [];
      byGroupUser[groupKey][r.user_id].push(avgUE);
    });
    // Asegurar que todos los días/semanas estén presentes en el eje X
    let groups: string[] = Object.keys(byGroupUser);
    if ((groupType === 'weekly' || groupType === 'daily') && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (groupType === 'weekly') {
        // Ajustar al primer día de la semana ISO (lunes)
        const getMonday = (d: Date) => {
          const date = new Date(d);
          const day = date.getDay();
          const diff = (day === 0 ? -6 : 1) - day;
          date.setDate(date.getDate() + diff);
          return date;
        };
        let current = getMonday(start);
        const weeksSet = new Set(groups);
        while (current <= end) {
          const iso = getISOWeek(current.toISOString().slice(0, 10));
          if (!weeksSet.has(iso)) {
            byGroupUser[iso] = {};
          }
          current.setDate(current.getDate() + 7);
        }
        groups = Object.keys(byGroupUser).sort();
      } else if (groupType === 'daily') {
        // Generar todos los días entre startDate y endDate
        let current = new Date(start);
        const daysSet = new Set(groups);
        while (current <= end) {
          const dayStr = current.toISOString().slice(0, 10);
          if (!daysSet.has(dayStr)) {
            byGroupUser[dayStr] = {};
          }
          current.setDate(current.getDate() + 1);
        }
        groups = Object.keys(byGroupUser).sort();
      }
    } else {
      groups = Object.keys(byGroupUser).sort();
    }

    // Etiquetas personalizadas para el eje X
    const dayNames = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // Domingo a Sábado
    const formatGroupLabel = (g: string) => {
      if (groupType === 'daily') {
        // g: YYYY-MM-DD
        const d = new Date(g);
        return dayNames[d.getDay()];
      } else if (groupType === 'weekly') {
        // g: YYYY-WWW
        // Mostrar S1, S2, S3... continuo en todo el rango
        const idx = groups.indexOf(g);
        return `S${idx + 1}`;
      } else if (groupType === 'monthly') {
        // g: YYYY-MM
        const [yyyy, mm] = g.split('-');
        const yy = yyyy.slice(-2);
        return `${mm}/${yy}`;
      } else {
        // yearly: g: YYYY
        return g;
      }
    };

    const xLabels = groups.map(formatGroupLabel);

    // 4) Unique users
    const users = Array.from(new Set(data.map(r => r.user_id)));

    // 5) Prepare an orange‐based palette
    const colorPalette = ['#FC7556', '#FC9556', '#FCB556', '#FCCA56', '#FCE856', '#FCFF56'];

    // 6) Build series
    const series = users.map((uid, idx) => {
      const name = userMap[uid] || `User ${uid}`;
      const baseColor = colorPalette[idx % colorPalette.length];
      // 20% opacity fill
      const areaColor = `${baseColor}33`;
      const vals = groups.map(g => {
        const arr = byGroupUser[g][uid] || [];
        if (!arr || arr.length === 0) return 0;
        const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
        return parseFloat(avg.toFixed(2));
      });
      return {
        name,
        type: 'line' as const,
        smooth: true,
        connectNulls: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: baseColor, width: 2 },
        itemStyle: { color: baseColor, borderColor: baseColor },
        areaStyle: { color: areaColor },
        emphasis: { focus: 'series' },
        data: vals,
      };
    });

    // 7) Aggregate for mini‑leaderboards
    const agg: Record<number, { am: number[]; ex: number[] }> = {};
    data.forEach(r => {
      if (!agg[r.user_id]) agg[r.user_id] = { am: [], ex: [] };
      agg[r.user_id].am.push(r.object.amabilidad.rating);
      agg[r.user_id].ex.push(r.object.experiencia.rating);
    });
    const leaders: Leader[] = users.map(uid => {
      const name = userMap[uid] || `User ${uid}`;
      const avgAm =
        agg[uid].am.reduce((a, b) => a + b, 0) / agg[uid].am.length;
      const avgEx =
        agg[uid].ex.reduce((a, b) => a + b, 0) / agg[uid].ex.length;
      return {
        user_id: uid,
        name,
        am: parseFloat(avgAm.toFixed(2)),
        ex: parseFloat(avgEx.toFixed(2)),
        all: parseFloat(((avgAm + avgEx) / 2).toFixed(2)),
      };
    });

    setTopAm([...leaders].sort((a, b) => b.am - a.am).slice(0, 3));
    setTopEx([...leaders].sort((a, b) => b.ex - a.ex).slice(0, 3));
    setTopAll([...leaders].sort((a, b) => b.all - a.all).slice(0, 3));

    // 8) Chart options
    setOption({
      title: {
        text:
          groupType === 'daily'
            ? 'Tendencia Diaria por Usuario'
            : groupType === 'weekly'
            ? 'Tendencia Semanal en el Mes por Usuario'
            : groupType === 'monthly'
            ? 'Tendencia Mensual por Usuario'
            : 'Tendencia Anual por Usuario',
        left: 'center',
        textStyle: { color: textColor, fontSize: 16 },
      },
      tooltip: { trigger: 'axis' },
      legend: {
        type: 'scroll',
        data: series.map(s => s.name),
        top: 30,
        textStyle: { color: textColor },
      },
      grid: { top: 70, left: 70, right: 50, bottom: 45 },
      xAxis: {
        type: 'category',
        name: 'Fecha',
        nameLocation: 'middle',
        nameGap: 30,
        data: xLabels,
        axisLine: { lineStyle: { color: textColor } },
        axisLabel: { 
          color: textColor,
          padding: [4, 8],
          borderRadius: 4,
          fontWeight: 600,
        },
        splitLine: { show: false },
      },
      yAxis: {
        name: 'Puntuación',
        nameLocation: 'middle',
        nameGap: 40,
        type: 'value',
        min: 'dataMin',
        max: 'dataMax',
        axisLine: { lineStyle: { color: textColor } },
        axisLabel: { color: textColor },
        splitLine: { show: true,lineStyle: { color: colorMode === 'light' ? '#e0e0e0' : '#444444' } },
      },
      series,
    });
  }, [reviews, userMap, startDate, endDate, textColor, colorMode]);

  if (!option) return <MiniLoadingSpinner />;

  // star if same #1 in amabilidad & experiencia
  const starId =
    topAm[0]?.user_id === topEx[0]?.user_id ? topAm[0].user_id : null;

  return (
    <div className="user-stats">
      {/* Chart */}
      <div className="chart">
        <ReactECharts
          option={option}
          style={{ height: '300px', width: '100%' }}
        />
      </div>

      {/* Mini‐leaderboards */}
      <div className="leaders-row">
        {[
          { title: 'Los más amables', arr: topAm, key: 'am' },
          { title: 'Mejor experiencia', arr: topEx, key: 'ex' },
          { title: 'Mejor promedio', arr: topAll, key: 'all' },
        ].map((blk, i) => (
          <div key={i} className="mini-card">
            <h4>{blk.title}</h4>
            <ol>
              {blk.arr.map((u, idx) => (
                <li key={u.user_id}>
                  <span className="rank">{idx + 1}</span>
                  <span className="name">{u.name}</span>
                  <span className="val">{(u as any)[blk.key].toFixed(2)}</span>
                  {starId === u.user_id && <FaStar className="star" />}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserStats;
